import React from "react";
import styled, { css, useTheme } from "styled-components";

import { Icons, IThemeProps } from "@/styles";
import Svg from "@/styles/utils/Svg";

type FormInputProps = {
  name?: string;
  label?: string;
  required?: { active: boolean; text: string };
  disabled?: boolean;
  defaultValue?: string;
  value?: string;
  valid?: boolean;
  validationMessage?: string;
  onChange?: (value: string) => void;
};

const FormInput: React.FC<FormInputProps> = ({
  name,
  label = "",
  disabled = false,
  defaultValue,
  required,
  valid,
  validationMessage,
  value = "",
  onChange = () => null,
}) => {
  const theme = useTheme();
  const generatedId = React.useId();
  const invalid = valid === false;
  const validationMessageId = `${name ?? generatedId}-validation-message`;
  const visibleValidationMessage = validationMessage ?? required?.text;

  return (
    <InputWrapper $invalid={invalid} $required={required}>
      {invalid && (
        <InputStatus aria-hidden="true">
          <Svg contents={Icons.Warning} height={18} width={18} />
        </InputStatus>
      )}
      {invalid && visibleValidationMessage && (
        <small
          id={validationMessageId}
          data-qa={`input.${name ?? "unknown"}.validation`}
          role="alert"
          style={{
            color: theme.colors.error,
            fontSize: theme.typography.h200,
            left: theme.spacing.desktop.s500,
            lineHeight: 1.3,
            position: "absolute",
            right: theme.spacing.desktop.s100,
            top: `calc(56px + ${theme.spacing.desktop.s100}px)`,
          }}
        >
          {visibleValidationMessage}
        </small>
      )}
      <Input
        data-qa={`input.${name}`}
        className={valid ? "" : "invalid"}
        id={name}
        name={name}
        disabled={disabled}
        defaultValue={defaultValue}
        $valid={valid}
        value={value}
        aria-describedby={
          invalid && visibleValidationMessage ? validationMessageId : undefined
        }
        aria-invalid={invalid}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
      />
      <InputLabel $hasValue={!!value}>{label}</InputLabel>
    </InputWrapper>
  );
};

export default FormInput;
export { FormInput };

const InputLabel = styled.span<IThemeProps<{ $hasValue: boolean }>>`
  position: absolute;
  top: ${(props) =>
    props.$hasValue ? 12 : props.theme.spacing.desktop.s600}px;
  left: 0;
  padding-left: ${(props) => props.theme.spacing.desktop.s500}px;
  width: auto;
  font-size: ${(props) =>
    props.$hasValue
      ? props.theme.typography.h100
      : props.theme.typography.h300};
  color: ${(props) =>
    props.theme.colors.inputColor
      ? props.theme.colors.inputColor
      : props.theme.colors.primary};
  transition: top 0.15s linear;
  cursor: text;
  text-transform: uppercase;

  ${(props) =>
    !props.theme.globals.isTouch &&
    `&:focus, &:hover {
        top: 12px;
        font-size: ${props.theme.typography.h100};
    }`}
`;

const InputStatus = styled.span<IThemeProps>`
  position: absolute;
  bottom: 15px;
  right: 10px;
  svg {
    fill: ${(props) => props.theme.colors.error};
  }
`;

const ErrorInput = css<IThemeProps>`
  border-color: ${(props) => props.theme.colors.error};

  ${(props) =>
    !props.theme.globals.isTouch &&
    `&:hover {
                border-color: ${props.theme.colors.error};
            }
            &:focus,
            &:active {
                border-bottom: 2px solid ${props.theme.colors.error};
            }`}
`;

export const InputWrapper = styled.label<
  IThemeProps<{
    $invalid?: boolean;
    $required?: { active: boolean; text: string };
  }>
>`
  flex: 1 1 auto;
  position: relative;
  width: auto;
  height: 56px;
  margin-bottom: ${(props) =>
    props.theme.spacing.desktop[props.$invalid ? "s900" : "s700"]}px;

  ${(props) =>
    props.$required?.active &&
    !props.$invalid &&
    `&:after {
            content: "* ${props.$required.text}";
            position: absolute;
            bottom: -16px;
            left: 10px;
            font-size: 11px;
            color: ${
              props.theme.colors.inputColor
                ? props.theme.colors.inputColor
                : props.theme.colors.primary
            };
        }`}
`;

type InputProps = IThemeProps<{
  $valid?: boolean;
}>;
export const Input = styled.input.attrs({ type: "text" })<InputProps>`
  font-size: ${(props) => props.theme.typography.h400};
  @media ${(props) => props.theme.breakpoints.mobile} {
    font-size: 16px;
  }
  width: 100%;
  height: 56px;
  padding-top: ${(props) => props.theme.spacing.desktop.s300}px;
  padding-right: ${(props) => props.theme.spacing.desktop.s300 * 2.5}px;
  padding-left: ${(props) => props.theme.spacing.desktop.s500}px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.83;
  letter-spacing: normal;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: 1px solid ${(props) => props.theme.colors.borderGrey};
  background-color: ${(props) => props.theme.colors.inputBackground};
  transition: all 0.2s ease-in;
  &:hover {
    background-color: rgb(33, 63, 89, 0.2);
    border-color: ${(props) => props.theme.colors.primary};
  }
  &:focus,
  &:active {
    background-color: rgb(33, 63, 89, 0.2);
    border-bottom: 2px solid ${(props) => props.theme.colors.primary};
  }
  &:focus ~ span,
  &:hover ~ span,
  &:active ~ span {
    top: 12px;
    font-size: ${(props) => props.theme.typography.h100};
  }
  ${(props) => props.$valid === false && ErrorInput}
`;
